// Editable Garment Line for Quote Summary
// Supports inline editing of Sell Price or Margin % with live feedback

import { useState, useEffect, useRef } from "react";
import { RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "./constants";

/**
 * EditableGarmentLine - Inline editable garment line for Quote Summary
 * 
 * Supports two editing modes:
 * - Price Mode (default): Edit sell price, margin updates automatically
 * - Margin Mode: Edit margin %, sell price updates automatically
 * 
 * Only ONE mode can be active at a time per line.
 */
export function EditableGarmentLine({ 
  line, 
  garment, 
  globalMargin, 
  onUpdateOverrides,
  isAdmin = false 
}) {
  // Editing mode: null (not editing), "price", or "margin"
  const [editMode, setEditMode] = useState(null);
  
  // Local input values for live editing
  const [priceInput, setPriceInput] = useState("");
  const [marginInput, setMarginInput] = useState("");
  
  const priceInputRef = useRef(null);
  const marginInputRef = useRef(null);
  
  // Determine current override state
  const hasMarginOverride = garment?.margin_override !== null && garment?.margin_override !== undefined;
  const hasPriceAdjustment = garment?.price_adjustment !== null && garment?.price_adjustment !== undefined && garment?.price_adjustment !== 0;
  const hasAnyOverride = hasMarginOverride || hasPriceAdjustment;
  
  // Get quantity and true cost from quote line
  const quantity = line?.quantity || 1;
  const lineTrueCost = line?.line_true_cost || 0;
  const unitTrueCost = quantity > 0 ? lineTrueCost / quantity : 0;
  
  // Calculate BASE price at global margin (WITHOUT any existing adjustments)
  // This is the price the line would have if no overrides were applied
  const basePriceAtGlobal = globalMargin < 1 && unitTrueCost > 0 
    ? unitTrueCost / (1 - globalMargin) 
    : unitTrueCost;
  
  // Current effective values from quote line
  const currentUnitPrice = line?.unit_sell_price || 0;
  const currentEffectiveMargin = line?.effective_margin !== null && line?.effective_margin !== undefined
    ? line.effective_margin
    : globalMargin;
  const lineTotal = line?.line_total || 0;
  
  // Get indicator type
  const getIndicator = () => {
    if (hasMarginOverride) return "margin";
    if (hasPriceAdjustment) return "price";
    return "global";
  };
  
  const indicator = getIndicator();
  
  // Initialize input values when entering edit mode
  useEffect(() => {
    if (editMode === "price") {
      setPriceInput(currentUnitPrice.toFixed(2));
      setTimeout(() => priceInputRef.current?.select(), 0);
    } else if (editMode === "margin") {
      setMarginInput((currentEffectiveMargin * 100).toFixed(0));
      setTimeout(() => marginInputRef.current?.select(), 0);
    }
  }, [editMode, currentUnitPrice, currentEffectiveMargin]);
  
  // Handle price edit - set price_adjustment to achieve EXACT target price
  // The entered value is the FINAL unit sell price, not an adjustment amount
  const handlePriceSubmit = () => {
    const targetPrice = parseFloat(priceInput);
    if (isNaN(targetPrice) || targetPrice <= 0) {
      setEditMode(null);
      return;
    }
    
    // Calculate price_adjustment needed to achieve exact target price
    // adjustment = target_price - base_price_at_global_margin
    // This ensures the backend calculates: base_price + adjustment = target_price
    const adjustment = targetPrice - basePriceAtGlobal;
    
    // If adjustment is effectively zero (target matches global margin price), clear overrides
    if (Math.abs(adjustment) < 0.01) {
      onUpdateOverrides({ margin_override: null, price_adjustment: null });
    } else {
      // Set price_adjustment, clear margin_override
      // The adjustment is always calculated from the BASE price (no existing override)
      onUpdateOverrides({ 
        margin_override: null, 
        price_adjustment: Math.round(adjustment * 100) / 100 
      });
    }
    
    setEditMode(null);
  };
  
  // Handle margin edit - set margin_override
  const handleMarginSubmit = () => {
    const targetMargin = parseFloat(marginInput);
    if (isNaN(targetMargin) || targetMargin < 0 || targetMargin >= 100) {
      setEditMode(null);
      return;
    }
    
    const marginDecimal = targetMargin / 100;
    
    // If margin is effectively the same as global, clear overrides
    if (Math.abs(marginDecimal - globalMargin) < 0.001) {
      onUpdateOverrides({ margin_override: null, price_adjustment: null });
    } else {
      // Set margin_override, clear price_adjustment
      onUpdateOverrides({ 
        margin_override: marginDecimal, 
        price_adjustment: null 
      });
    }
    
    setEditMode(null);
  };
  
  // Handle reset to global
  const handleReset = () => {
    onUpdateOverrides({ margin_override: null, price_adjustment: null });
    setEditMode(null);
  };
  
  // Handle key events
  const handleKeyDown = (e, submitFn) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitFn();
    } else if (e.key === "Escape") {
      setEditMode(null);
    }
  };
  
  // Calculate live preview values while editing
  const getLivePreview = () => {
    if (editMode === "price") {
      const targetPrice = parseFloat(priceInput);
      if (isNaN(targetPrice) || targetPrice <= 0) return null;
      
      // Calculate effective margin for this price using true cost
      const effectiveMargin = targetPrice > 0 && unitTrueCost > 0 
        ? (targetPrice - unitTrueCost) / targetPrice 
        : 0;
      
      return {
        unitPrice: targetPrice,
        lineTotal: targetPrice * quantity,
        margin: effectiveMargin
      };
    } else if (editMode === "margin") {
      const targetMargin = parseFloat(marginInput) / 100;
      if (isNaN(targetMargin) || targetMargin < 0 || targetMargin >= 1) return null;
      
      // Calculate price for this margin using true cost
      const unitPrice = unitTrueCost > 0 ? unitTrueCost / (1 - targetMargin) : 0;
      
      return {
        unitPrice: unitPrice,
        lineTotal: unitPrice * quantity,
        margin: targetMargin
      };
    }
    return null;
  };
  
  const livePreview = getLivePreview();
  
  // Display values (use live preview if editing, otherwise actual)
  const displayUnitPrice = livePreview?.unitPrice ?? currentUnitPrice;
  const displayLineTotal = livePreview?.lineTotal ?? lineTotal;
  const displayMargin = livePreview?.margin ?? currentEffectiveMargin;
  
  // Badge styling
  const getBadgeStyle = () => {
    if (indicator === "margin") {
      return "bg-blue-100 text-blue-700 border-blue-300";
    } else if (indicator === "price") {
      return "bg-emerald-100 text-emerald-700 border-emerald-300";
    }
    return "bg-slate-100 text-slate-600 border-slate-300";
  };
  
  const getBadgeLabel = () => {
    if (indicator === "margin") return "Custom Margin";
    if (indicator === "price") return "Custom Price";
    return "Global";
  };

  return (
    <div 
      className={`py-2 border-b last:border-b-0 ${
        hasAnyOverride ? 'bg-blue-50/50 -mx-2 px-2 rounded' : ''
      }`}
      data-testid={`editable-line-${line?.id}`}
    >
      {/* Top row: Name + Indicator + Line Total */}
      <div className="flex justify-between items-start mb-1 gap-2">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1 overflow-hidden">
          <span className="text-sm font-medium truncate max-w-[200px] sm:max-w-[250px]" title={line?.display_name || line?.name || garment?.name || "Garment"}>
            {line?.display_name || line?.name || garment?.name || "Garment"}
          </span>
          
          {/* Indicator Badge */}
          {isAdmin && (
            <Badge 
              variant="outline" 
              className={`text-[9px] px-1.5 py-0 ${getBadgeStyle()}`}
              data-testid={`line-indicator-${line?.id}`}
            >
              {getBadgeLabel()}
            </Badge>
          )}
          
          {/* Reset Button (only show if has override) */}
          {isAdmin && hasAnyOverride && (
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-[10px] text-muted-foreground hover:text-foreground"
              onClick={handleReset}
              data-testid={`reset-line-${line?.id}`}
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
          )}
        </div>
        
        {/* Line Total */}
        <span className="font-mono text-sm font-semibold shrink-0">
          {formatCurrency(displayLineTotal)}
        </span>
      </div>
      
      {/* Bottom row: Qty × Price + Margin */}
      <div className="flex justify-between items-center text-xs">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">{quantity} ×</span>
          
          {/* Editable Price */}
          {isAdmin && editMode === "price" ? (
            <div className="relative inline-flex items-center">
              <span className="text-muted-foreground mr-0.5">$</span>
              <Input
                ref={priceInputRef}
                type="number"
                step="0.01"
                min="0"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onBlur={handlePriceSubmit}
                onKeyDown={(e) => handleKeyDown(e, handlePriceSubmit)}
                className="w-20 h-6 text-xs font-mono px-1 py-0"
                data-testid={`price-input-${line?.id}`}
              />
            </div>
          ) : (
            <button
              onClick={() => isAdmin && setEditMode("price")}
              className={`font-mono font-medium ${
                isAdmin ? 'hover:bg-slate-100 px-1 py-0.5 rounded cursor-pointer' : ''
              }`}
              disabled={!isAdmin}
              data-testid={`price-display-${line?.id}`}
            >
              {formatCurrency(displayUnitPrice)}
            </button>
          )}
          
          <span className="text-muted-foreground">ea</span>
        </div>
        
        {/* Editable Margin (admin only) */}
        {isAdmin && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-[10px]">Margin:</span>
            
            {editMode === "margin" ? (
              <div className="relative inline-flex items-center">
                <Input
                  ref={marginInputRef}
                  type="number"
                  step="1"
                  min="0"
                  max="99"
                  value={marginInput}
                  onChange={(e) => setMarginInput(e.target.value)}
                  onBlur={handleMarginSubmit}
                  onKeyDown={(e) => handleKeyDown(e, handleMarginSubmit)}
                  className="w-12 h-5 text-[10px] font-mono px-1 py-0"
                  data-testid={`margin-input-${line?.id}`}
                />
                <span className="text-muted-foreground ml-0.5 text-[10px]">%</span>
              </div>
            ) : (
              <button
                onClick={() => setEditMode("margin")}
                className="font-mono text-[10px] hover:bg-slate-100 px-1 py-0.5 rounded cursor-pointer"
                data-testid={`margin-display-${line?.id}`}
              >
                {(displayMargin * 100).toFixed(1)}%
              </button>
            )}
          </div>
        )}
        
        {/* Non-admin: just show margin */}
        {!isAdmin && (
          <span className="text-muted-foreground text-[10px]">
            Margin: {(displayMargin * 100).toFixed(1)}%
          </span>
        )}
      </div>
      
      {/* Decoration summary if present */}
      {line?.decoration_summary && (
        <p className="text-[10px] text-blue-600 mt-1">
          Decoration: {line.decoration_summary}
        </p>
      )}
    </div>
  );
}

export default EditableGarmentLine;
